import {
  comparisonStore,
} from "@/features/widgets/components/Comparison/utils/comparisonStore.tsx";
import { useTranslation } from "react-i18next";
import useConfig from "@/features/config/hooks/useConfig.tsx";
import { Fragment, useLayoutEffect, useRef, useState } from "react";
import useTrackEvent from "@/features/widgets/hooks/useTrackEvent.tsx";
import CheckSlide from "@/components/ui/CheckSlide/CheckSlide.tsx";
import Loader from "@/components/ui/Loader/Loader.tsx";
import DragScroll from "@/components/ui/DragToScroll/DragToScroll.tsx";
import Messages from "@/components/ui/Messages/Messages.tsx";
import Tooltip from "@/components/ui/Tooltip/Tooltip.tsx";
import Html from "@/components/ui/Html/Html.tsx";

import IconYes from "@/assets/icons/yes.svg?react";
import IconNo from "@/assets/icons/no.svg?react";
import IconClose from "@/assets/icons/close.svg?react";
import IconInfo from "@/assets/icons/info.svg?react";
import IconWand from "@/assets/icons/wand.svg?react";
import PlaceHolder from "@/assets/icons/placeholder.svg?react";
import uiHelper from "@/utils/uiHelper.tsx";
import { Rating } from "@/components/ui/Rating/Rating.tsx";
import { AdviceAttributesResult, Product, ProductAttribute } from "@/features/widgets/components/Comparison/types/ComparisonTypes";
import { mergeProductData } from "@/features/widgets/components/Comparison/utils/productHelper.tsx";
import Button from "@/components/ui/Buttons/Button/Button.tsx";

type CompareStatus = {
  is_learning: boolean;
  is_preview: boolean;
  multiple_categories: boolean;
  missing_products: boolean;
}

type CompareConfig = {
  features: {
    compare_advanced?: boolean;
    product_advisor?: boolean;
  }
}

export type CompareResult = {
  products: Product[],
  attributes: ProductAttribute[],
  config: CompareConfig;
  status: CompareStatus;
};

type ComparisonTableProps = {
  products: Product[];
  data: CompareResult | null;
  loading: boolean;
  errors: string[];
  warnings: string[];
  attributeAdvice: AdviceAttributesResult | null;
  attributeAdviceLoading: boolean;
  advisorEnabled: boolean;
  bestProductId?: number | null;
  onOpenAdvice: () => void;
}

const CLASS_PREFIX = `ws-widgets-comparison`;

const ATTRIBUTES_COLLAPSE_THRESHOLD = 4;

export const ComparisonTable = ({ products, data, loading, errors, warnings, attributeAdvice, attributeAdviceLoading, advisorEnabled, bestProductId = null, onOpenAdvice }: ComparisonTableProps) => {
  const config = useConfig();
  const [showDiffOnly, setShowDiffOnly] = useState<boolean>(false);
  const [selectedAttribute, setSelectedAttribute] = useState<ProductAttribute | null>(null);
  const [attributesExpanded, setAttributesExpanded] = useState<boolean>(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number>();
  const [expandedHeight, setExpandedHeight] = useState<number>();
  const tableRef = useRef<HTMLDivElement>(null);
  const collapseBoundaryRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();

  const hasAttributes = !!(!loading && data?.attributes?.length && data?.attributes?.length > 0);
  const isAttributesCollapsible = !showDiffOnly && (data?.attributes?.length ?? 0) > ATTRIBUTES_COLLAPSE_THRESHOLD;

  useLayoutEffect(() => {
    if (!isAttributesCollapsible) {
      return;
    }

    const table = tableRef.current;
    const boundary = collapseBoundaryRef.current;
    if (!table || !boundary) {
      return;
    }

    const measure = () => {
      // Measured relative to the table (its nearest positioned ancestor,
      // since both are direct children of it), so this stays correct
      // regardless of the table's current scroll position.
      const nextCollapsedHeight = boundary.offsetTop + boundary.offsetHeight;
      const nextExpandedHeight = table.scrollHeight;

      if (nextCollapsedHeight > 0) {
        setCollapsedHeight(nextCollapsedHeight);
      }
      if (nextExpandedHeight > 0) {
        setExpandedHeight(nextExpandedHeight);
      }
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(table);
    return () => resizeObserver.disconnect();
  }, [data?.attributes, isAttributesCollapsible]);

  const getAttributeAdvice = (attribute: ProductAttribute) => {
    const result = attributeAdvice?.attributes.find(
      item => item.id === attribute.id || item.name.toLowerCase().trim() === attribute.name.toLowerCase().trim()
    );

    return result?.advice ?? attribute.description ?? '';
  }

  const getProductData = (product: Product) => mergeProductData(product, data);

  const isAdvisorSelected = (product: Product) => bestProductId !== null && product.id === bestProductId;

  if (data?.status.is_learning) {
    console.warn('Domain is in learning mode, product attributes are not yet available!');
  }

  const containerClasses = [
    `${ CLASS_PREFIX }-table`,
    loading ? `${ CLASS_PREFIX }-table--loading` : ``,
    isAttributesCollapsible ? `${ CLASS_PREFIX }-table--collapsible` : ``,
    isAttributesCollapsible && attributesExpanded ? `${ CLASS_PREFIX }-table--expanded` : ``,
  ];

  const maxHeight = isAttributesCollapsible
    ? (attributesExpanded ? expandedHeight : collapsedHeight)
    : undefined;

  return (
    <>
      { (hasAttributes || advisorEnabled) &&
        <div
          className={ `${ CLASS_PREFIX }-table-controls` }>
          <div className={ `${ CLASS_PREFIX }-table-controls__checkbox` }>
            { hasAttributes ?
              <CheckSlide
                id={ `ws-compare-diff-toggle` }
                label={ t('comparison.diff_toggle') }
                onChange={ () => {
                  setShowDiffOnly(!showDiffOnly);
                } }
                checked={ showDiffOnly }
              />
              :
              <span>&nbsp;</span>
            }
          </div>
          <div className={ `${ CLASS_PREFIX }-table-controls__right` }>
            { advisorEnabled &&
              <Button
                className={ `${ CLASS_PREFIX }-table-controls__advice-button` }
                onClick={ onOpenAdvice }
                variant={ `secondary` }
              >
                <span className={ `${ CLASS_PREFIX }-table-controls__advice-icon` }>
                  <IconWand/>
                </span>
                <span className={ `${ CLASS_PREFIX }-table-controls__advice-button-text` }>
                  { t(`comparison.advice.title`) }
                </span>
              </Button>
            }
          </div>
        </div>
      }

      { errors.length > 0 && <Messages type={ `error` } messages={ errors }/> }
      { warnings.length > 0 && <Messages type={ `warning` } messages={ warnings }/> }
      <DragScroll
        ref={ tableRef }
        axis={ `x` }
        className={ containerClasses.join(` `) }
        style={ maxHeight !== undefined ? { maxHeight } : undefined }
      >
        { data?.status.is_preview && <PreviewNotification/> }

        { loading && <Loader overlay={ true }/> }
        <div className={ `${ CLASS_PREFIX }-table__header` }>
          <div className={ `${ CLASS_PREFIX }-table__row ${ CLASS_PREFIX }-table__images` }>

            { products.map((item, index) => {
              const product = getProductData(item);
              if (!product) {
                return;
              }

              const handleRemove = () => {
                comparisonStore.remove(product.url);
                trackEvent(
                  'click',
                  'comparison_dialog_remove',
                  product.name,
                  comparisonStore.getProducts(),
                );
              }

              const classes = [
                `${ CLASS_PREFIX }-table-column`,
                `${ CLASS_PREFIX }-table__column`,
              ]

              return (
                <div key={ index } data-column-product-id={ product.id } className={ classes.join(` `) }>
                  <div className={ `${ CLASS_PREFIX }-table__image-wrapper` }>
                    <a href={ product.url } rel="noopener noreferrer">
                      <div className={ `${ CLASS_PREFIX }-table__image` }>
                        { product?.image ?
                          <img src={ product.image } alt={ product.name }/>
                          :
                          <PlaceHolder/>
                        }
                      </div>
                    </a>
                    <button className={ `${ CLASS_PREFIX }-table__product-remove` } onClick={ handleRemove }>
                      <IconClose/>
                    </button>
                  </div>
                </div>
              )
            })
            }
          </div>
          <div className={ `${ CLASS_PREFIX }-table-row ${ CLASS_PREFIX }-table__row ${ CLASS_PREFIX }-table__titles` }>
            {
              products.map(
              (item, index) => {
                const product = getProductData(item);
                if (!product) {
                  return;
                }

                const price = product?.price ? uiHelper.formatPrice(product.price, config.language, config.currency) : null;
                const titleColumnClasses = [
                  `${ CLASS_PREFIX }-table-column`,
                  `${ CLASS_PREFIX }-table__column`,
                  isAdvisorSelected(product) ? `${ CLASS_PREFIX }-table-column--advisor-selected` : ``,
                ];

                return (
                  <div key={ index } data-column-product-id={ product.id } className={ titleColumnClasses.join(` `) }>
                    <div className={ `${ CLASS_PREFIX }-table__titlebar` }>
                      { isAdvisorSelected(product) &&
                        <div className={ `${ CLASS_PREFIX }-table__advisor-badge` }>
                          { t(`comparison.advice.best_choice`) }
                        </div>
                      }
                      <div className={ `${ CLASS_PREFIX }-table__title` }>
                        <a href={ product.url } rel="noopener noreferrer">
                          { product.name }
                        </a>
                      </div>
                      <div className={ `${ CLASS_PREFIX }-table__rating` }>
                        <Rating
                          value={ product.average_rating ? product.average_rating : 0 }
                          ratingCount={ product.number_of_ratings ? product.number_of_ratings : 0 }
                        />
                      </div>
                      <div className={ `${ CLASS_PREFIX }-table__cta ${ CLASS_PREFIX }-table-cta` }>
                        <div className={ `${ CLASS_PREFIX }-table-cta__price` }>
                          { price }
                        </div>
                        { product.url &&
                          <div className={ `${ CLASS_PREFIX }-table-cta__link` }>
                            <a className={ `ws-widgets-button ws-widgets-button--primary` }
                               href={ product.url }
                               onClick={ () =>
                                 trackEvent(
                                   `click`,
                                   `comparison_dialog_buy`,
                                   product.name,
                                   products,
                                 )
                              }>
                              { t(`core.button.buy`) }
                            </a>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                )
              })
            }
          </div>
        </div>

        { data?.attributes.map(
          (attribute, index) => {
            const advice = getAttributeAdvice(attribute);

            const rowClasses = [
              `${ CLASS_PREFIX }-table__row`,
              `${ CLASS_PREFIX }-table-row`,
              `${ CLASS_PREFIX }-table-row`,
              `${ CLASS_PREFIX }-table-row--tid--${ attribute.id }`,
              `${ CLASS_PREFIX }-table-row--${ attribute.description ? `with` : `without` }-description`,
              `${ CLASS_PREFIX }-table-row--${ (attribute.similarity !== undefined && attribute.similarity < 100) ? `with` : `without` }-difference`,
              `${ CLASS_PREFIX }-table-row--${ (advice && !attributeAdviceLoading) ? `with` : `without` }-advice`,
              showDiffOnly && attribute.similarity === 100 ? `${ CLASS_PREFIX }-table__row--hidden` : ``,
              selectedAttribute?.id === attribute.id ? `${ CLASS_PREFIX }-table__row--selected` : ``,
            ];

            const labelRowClasses = [...rowClasses, [
              `${ CLASS_PREFIX }-table-row--attribute-name`,
            ]];

            const labelClasses = [
              `${ CLASS_PREFIX }-table-column`,
              `${ CLASS_PREFIX }-table-column--attribute-name`,
              `${ CLASS_PREFIX }-table-column__attribute-name--tid--${ attribute.id }`,
              `${ CLASS_PREFIX }-table-column__attribute-name--${ attribute.description ? `with` : `without` }-description`,
              `${ CLASS_PREFIX }-table-column__attribute-name--${ (advice && !attributeAdviceLoading) ? `with` : `without` }-advice`,
            ];

            return (
              <Fragment key={ attribute.id }>
                <div className={ labelRowClasses.join(` `) }>
                  <div className={ labelClasses.join(` `) }>
                    { attribute.name }

                    { (attribute.description || (advice && !attributeAdviceLoading)) &&
                      <Tooltip
                        trigger={ <IconInfo/> }
                        onOpenChange={ (visible) => setSelectedAttribute(visible ? attribute : null) }
                      >
                        <Html value={ advice ? advice : attribute.description }/>
                      </Tooltip>
                    }
                  </div>
                </div>

                <div key={ `${ attribute.id }-${ attribute.name }` }
                     ref={ index === ATTRIBUTES_COLLAPSE_THRESHOLD - 1 ? collapseBoundaryRef : undefined }
                     className={ rowClasses.join(` `) }
                     data-similarity={ attribute.similarity }
                >

                  { products.map(
                    (item, index) => {
                      const product = getProductData(item);
                      if (!product) {
                        return;
                      }

                      const valueClasses = [
                        `${ CLASS_PREFIX }-table-column`,
                        `${ CLASS_PREFIX }-table-column--${ (attribute.similarity !== undefined && attribute.similarity < 100) ? `with` : `without` }-difference`,
                        `${ CLASS_PREFIX }-table__column`,
                        `${ CLASS_PREFIX }-table__column--${ (attribute.similarity !== undefined && attribute.similarity < 100) ? `with` : `without` }-difference`,
                        `${ CLASS_PREFIX }-table__attribute-value`,
                        `${ CLASS_PREFIX }-table__attribute-value--tid--${ attribute.id }`,
                      ];

                      return (
                        <div key={ index } data-column-product-id={ product.id } className={ valueClasses.join(` `) }>
                          <AttributeValue product={ product } attribute={ attribute }/>
                        </div>
                      )
                    })
                  }
                </div>
              </Fragment>
            )
          })
        }
      </DragScroll>

      { isAttributesCollapsible &&
        <button
          className={ `${ CLASS_PREFIX }-table__attributes-toggle` }
          onClick={ () => setAttributesExpanded(!attributesExpanded) }
        >
          { attributesExpanded
            ? t(`comparison.table.show_fewer_attributes`)
            : t(`comparison.table.show_all_attributes`)
          }
        </button>
      }
    </>
  )
}

type AttributeValueProps = {
  product: Product;
  attribute: ProductAttribute;
}

const AttributeValue = ({ product, attribute }: AttributeValueProps) => {
  let productAttribute = product.attributes?.find(
    item => item.id && item.id === attribute.id
  );

  if (!productAttribute) {
    productAttribute = product.attributes?.find(
      item => item.name === attribute.name
    );
  }

  let icon = null;
  let text = null;

  if (productAttribute?.type === 'boolean' && productAttribute?.value !== null) {
    icon = productAttribute?.value ?
      <div className={ `${ CLASS_PREFIX }-table__icon ${ CLASS_PREFIX }-table__icon--yes` }>
        <IconYes/>
      </div> :
      <div className={ `${ CLASS_PREFIX }-table__icon ${ CLASS_PREFIX }-table__icon--no` }>
        <IconNo/>
      </div>;
  }
  else if (productAttribute?.type === 'range' && productAttribute?.value !== null) {
    text = productAttribute?.value.join(' - ');
  }
  else if (productAttribute?.type === 'list' && productAttribute?.value !== null) {
    text = productAttribute?.value.join(' / ');
  }
  else if (productAttribute?.value !== null) {
    text = productAttribute?.value?.toString();
  }

  if (!productAttribute || (!text && !icon)) {
    return (
      <div className={ `${ CLASS_PREFIX }-table__icon ${ CLASS_PREFIX }-table__icon--unknown` }>
        -
      </div>
    )
  }

  return (
    <>
      { text } { productAttribute.unit }
      { icon }
    </>
  )
}

const PreviewNotification = () => {
  const { t } = useTranslation();
  return (
    <div className={ `${ CLASS_PREFIX }-preview` }>
      <h2 className={ `${ CLASS_PREFIX }-preview__title` }>
        { t('preview.title') }
      </h2>
      <p className={ `${ CLASS_PREFIX }-preview__text` }>
        { t('preview.text') }
      </p>
    </div>
  )
}
